/**
 * What a photo or sketch is called once it leaves the app.
 *
 * Every downloaded file leads with the export moment, its Maßnahme and its Lage
 * — "2026_08_11-14_35-M-2024-0123-1+Bef_7000-Fl_1-Pl_1_IMG_0042.jpg" — and the
 * original name stays on the end, so several pictures of one Planum keep telling
 * themselves apart.
 *
 * The whole name is plain ASCII (see `asciiFileName`): these files are handed on
 * to archives and to other people's machines, where an umlaut or a space in a
 * file name is still a way to lose one.
 */

import type { MediaDocType } from '@ast-data/shared';
import { asciiFileName, stampFileName } from '../../utils/downloadFile';

/** Where a picture was taken — the Lage its file name leads with. */
export interface MediaLocation {
    /** The owning Befund's number. */
    findingNumber?: string;
    /**
     * The owning Gesamtbeschreibung's number — instead of a Befundnummer, never
     * beside it: a picture hangs off the one record or the other.
     */
    overallDescriptionNumber?: string;
    /** The owning Probe's day (YYYY-MM-DD) — beside the Lage of its description. */
    sampleDate?: string;
    /** Fläche. */
    area?: string;
    /** Planumnummer — set this OR `profileNumber`, not both. */
    planumNumber?: string;
    /** Profilnummer — set this OR `planumNumber`, not both. */
    profileNumber?: string;
    /** The Tagebuch day (YYYY-MM-DD) — a Tagebuch picture has no Lage. */
    diaryDate?: string;
}

/** The extension a stored mime type saves under ('' when it says nothing). */
export function extensionFor(mimeType: string): string {
    if (mimeType === 'image/jpeg') {
        return '.jpg';
    }
    const subtype = /^image\/([a-z0-9.+-]+)$/i.exec(mimeType)?.[1];
    return subtype ? `.${subtype}` : '';
}

/**
 * The word a Lage value may repeat, in every spelling the fields see: the
 * record's own ("Fläche 1"), the pickers' ("Fl. 1"), and the same without a
 * separator ("Fl1").
 */
const REPEATED_LABEL =
    /^(befund|bef|flaeche|fläche|fl|planum|pl|profil|pr)\.?[\s_-]*/i;

/**
 * A Lage value cut down to the part that identifies it: "Fläche Nord" is written
 * "Fl_Nord", not "Fl_Flaeche_Nord" — the segment's own label already says which
 * axis this is.
 *
 * A value that is NOTHING BUT the word ("Planum", with no number behind it) keeps
 * it — better a repeated label than a segment that says nothing.
 */
function bareValue(value: string): string {
    const stripped = value.trim().replace(REPEATED_LABEL, '');
    return asciiFileName(stripped || value, '');
}

/**
 * What stands between the Maßnahmennummer and the Lage. A THIRD character,
 * because the other two are spoken for: '_' separates a Lage label from its
 * value (Bef_7000), '-' separates the Lage's parts — and the Maßnahmennummer
 * contains '-' itself (M-2024-0123-1).
 *
 * '+' is legal on NTFS, exFAT, ext4 and APFS and inert to bash, zsh and
 * PowerShell unquoted. The one place it is not is an URL QUERY string, where it
 * decodes to a space — these names never travel through one (they are a
 * download attribute on a blob); if that changes, swap this for '@'.
 */
const MEASURE_SEPARATOR = '+';

/**
 * "M-2024-0123-1+Bef_7000-Fl_1-Pl_1" — the Maßnahme and the Lage, the part of a
 * file name before the picture's own. A part with no value is left out rather
 * than written empty ("…+Bef_7000-Pl_1", not "…+Bef_7000-Fl_-Pl_1"); with
 * nothing known at all this returns '' and the caller keeps the bare name.
 */
export function mediaNamePrefix(
    measureNumber: string,
    {
        findingNumber,
        overallDescriptionNumber,
        sampleDate,
        area,
        planumNumber,
        profileNumber,
        diaryDate
    }: MediaLocation
): string {
    const part = (label: string, value: string | undefined) =>
        value?.trim() ? `${label}_${bareValue(value)}` : '';
    const location = [
        part('Bef', findingNumber),
        // "Gesb" is what this record is called everywhere else — the ExcaBook
        // column, the grid header, the form's label.
        part('Gesb', overallDescriptionNumber),
        part('Fl', area),
        planumNumber?.trim()
            ? part('Pl', planumNumber)
            : part('Pr', profileNumber),
        // Spelled out: "Pr" is the Profil already.
        part('Probe', sampleDate?.replace(/-/g, '_')),
        // Underscores, not the date's own dashes: '-' separates the parts.
        part('TB', diaryDate?.replace(/-/g, '_'))
    ]
        .filter(Boolean)
        .join('-');
    return [asciiFileName(measureNumber, ''), location]
        .filter(Boolean)
        .join(MEASURE_SEPARATOR);
}

/**
 * What a photo or sketch saves as, under `prefix`. The uploaded file's own name
 * wins for the picture's part, so a downloaded copy stays recognisable next to
 * the original. A sketch has none, so it falls back to its Bildunterschrift,
 * and finally to what kind of thing it is.
 */
export function mediaFileName(
    media: MediaDocType,
    prefix: string,
    /**
     * A sketch is re-rendered on download and may come out in a different
     * format than the one stored, so the caller can override the type.
     */
    mimeType?: string
): string {
    const extension = extensionFor(mimeType ?? media.mimeType);
    // The stored bytes decide the extension, not the name the file arrived
    // under: a phone's HEIC is transcoded to JPEG on the way in, and ".HEIC"
    // over JPEG bytes gives a file nothing will open.
    const given = (media.originalName || media.caption || media.kind).replace(
        /\.[^.]+$/,
        ''
    );
    const base = asciiFileName(given, media.id);
    const name = [prefix, base].filter(Boolean).join('_');
    return `${stampFileName(name)}${extension}`;
}

/**
 * What a Bildtafel saves as, under `prefix`: its name on the sheet ("Bildtafel
 * 2") and its title, as a JPEG.
 */
export function plateFileName(
    prefix: string,
    plateName: string,
    caption: string
): string {
    const base = asciiFileName(
        [plateName, caption.trim()].filter(Boolean).join(' '),
        'plate'
    );
    const name = [prefix, base].filter(Boolean).join('_');
    return `${stampFileName(name)}.jpg`;
}
